import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Sparkle, Briefcase, Users, ArrowRight, ArrowLeft, Rocket } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../api";
import { useAuth } from "../AuthContext";

const TYPES = [
  { key: "brand",  label: "Brand",  icon: Briefcase, accent: "#22D3EE", gradient: "from-[#22D3EE] to-[#8B5CF6]",
    tagline: "Run your own ambassador program end-to-end." },
  { key: "agency", label: "Agency", icon: Users,     accent: "#A3E635", gradient: "from-[#A3E635] to-[#22D3EE]",
    tagline: "Execute creator campaigns for multiple client brands." },
];

const PLANS = [
  { name: "Everything, free during launch", price: "₹0", best: true,
    points: ["Unlimited projects", "Unlimited creators", "White-label branding", "All analytics & reports"] },
  { name: "Paid packages", price: "Coming soon", best: false,
    points: ["Priority support", "Custom domain", "Team invites & SSO", "Dedicated CSM"] },
];

export default function GetStarted() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("brand");
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accent, setAccent] = useState("#3B82F6");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="app-bg min-h-screen grid place-items-center text-muted">Loading…</div>;
  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/me";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/organizations/signup", {
        org_name: orgName.trim(), org_type: type,
        name: name.trim(), email: email.trim().toLowerCase(),
        password, accent_color: accent,
      });
      setUser(data);
      toast.success(`Welcome, ${data.name}! Your workspace is live.`);
      nav("/client");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  const active = TYPES.find(t => t.key === type) || TYPES[0];

  return (
    <div className="app-bg min-h-screen p-4 sm:p-6 lg:p-10" data-testid="get-started">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
              <Sparkle size={16} weight="fill" color="white" />
            </div>
            <div className="text-white font-black tracking-tight">Crew<span className="text-[#22D3EE]">HQ</span></div>
          </Link>
          <Link to="/" className="text-xs text-muted hover:text-white flex items-center gap-1.5" data-testid="get-started-back">
            <ArrowLeft size={12} weight="bold" /> Back to home
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left: pitch + plans */}
          <div className="space-y-6">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#A3E635] flex items-center gap-2">
                <Rocket size={12} weight="fill" /> Start free — no credit card
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.02em] text-white leading-[1.05]">
                Launch your{" "}
                <span className="bg-gradient-to-r from-[#22D3EE] via-[#A3E635] to-[#EC4899] bg-clip-text text-transparent">
                  {active.label.toLowerCase()} workspace
                </span>{" "}
                in 60 seconds.
              </h1>
              <p className="mt-3 text-muted text-sm sm:text-base">
                Your own dashboard, your logo, your accent color. Add creators, run campaigns, pay them — no spreadsheets.
              </p>
            </div>

            <div className="grid gap-2">
              {PLANS.map(p => (
                <div key={p.name} className={`glass-soft p-4 flex items-center gap-4 ${p.best ? "ring-1 ring-[#22D3EE]/40" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-white font-black">{p.name}</div>
                      {p.best && <span className="pill text-[10px]" style={{color:"#0E7490", background:"#22D3EE1a", borderColor:"#22D3EE55"}}>Popular</span>}
                    </div>
                    <div className="text-[11px] text-muted mt-1">{p.points.join(" • ")}</div>
                  </div>
                  <div className="text-white font-black text-sm whitespace-nowrap">{p.price}</div>
                </div>
              ))}
            </div>

            <div className="text-[11px] text-muted">
              Already have an account? <Link to="/login" className="text-[#22D3EE] font-bold" data-testid="get-started-signin-link">Sign in</Link>
            </div>
          </div>

          {/* Right: signup form */}
          <div className="glass p-6 sm:p-8 shadow-deep">
            <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-2xl mb-6">
              {TYPES.map(t => {
                const Icon = t.icon; const isActive = type === t.key;
                return (
                  <button key={t.key} onClick={() => setType(t.key)} type="button"
                    data-testid={`org-type-${t.key}`}
                    className={`relative flex items-center justify-center gap-2 py-3 px-3 rounded-xl text-sm font-bold transition-all
                      ${isActive ? "text-white" : "text-muted hover:text-white"}`}
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${t.accent}22 0%, ${t.accent}11 100%)`,
                      boxShadow: `0 0 24px ${t.accent}33, inset 0 0 0 1px ${t.accent}55`,
                    } : {}}>
                    <Icon size={16} weight={isActive ? "fill" : "bold"} color={isActive ? t.accent : undefined} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="text-xs text-muted mb-5 flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full" style={{background: active.accent, boxShadow:`0 0 8px ${active.accent}`}} />
              {active.tagline}
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">
                  {type === "agency" ? "Agency name" : "Brand / company name"}
                </label>
                <input required value={orgName} onChange={e => setOrgName(e.target.value)}
                  placeholder={type === "agency" ? "Studio Nova" : "Neon Threads"}
                  className="input-base mt-2" data-testid="signup-org-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-muted">Your name</label>
                  <input required value={name} onChange={e => setName(e.target.value)}
                    placeholder="Aria Mehta" className="input-base mt-2" data-testid="signup-owner-name" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-bold text-muted">Accent color</label>
                  <div className="mt-2 flex items-center gap-3">
                    <input type="color" value={accent} onChange={e => setAccent(e.target.value)}
                      className="h-10 w-16 rounded-lg bg-transparent border border-white/10 cursor-pointer"
                      data-testid="signup-accent" />
                    <div className="text-xs text-muted font-mono">{accent}</div>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Work email</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" className="input-base mt-2" data-testid="signup-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Password</label>
                <input type="password" required minLength={6} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="At least 6 chars"
                  className="input-base mt-2" data-testid="signup-password" />
              </div>

              <button disabled={busy} type="submit" data-testid="signup-submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{ background: `linear-gradient(90deg, ${active.accent} 0%, #8B5CF6 100%)`,
                         boxShadow: `0 8px 24px ${active.accent}55` }}>
                {busy ? "Creating workspace…" : `Create ${active.label.toLowerCase()} workspace`} <ArrowRight size={16} weight="bold" />
              </button>
              <p className="text-[11px] text-muted text-center leading-relaxed">
                Free forever plan. Upgrade any time.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
